<template>
  <div class="sbar-wrapper" :class="{ 'sbar-closed': !appStore.sidebarIsOpen }">
    <div class="sbar-header">
      <img v-if="isDark && appStore.sidebarIsOpen" src="/images/logo-font-white.svg" alt="Yuzu logo" class="logo" />
      <img v-else-if="!isDark && appStore.sidebarIsOpen" src="/images/logo-font-black.svg" alt="Yuzu logo" class="logo" />
    </div>
    <transition name="fade" mode="out-in">
      <div class="sbar-body">
        <div v-if="appStore.currentOrganisationId" class="sbar-separator">{{ $t('app.sidebar.context.organisation') }}</div>
        <Item v-if="appStore.currentOrganisationId" data-step="workspace-list" routeName="space-selector" icon="bi-folder" :label="$t('app.sidebar.items.spaces')"></Item>
        <div v-if="appStore.currentSpaceId && !isSpacePending" class="sub-section">
          <div class="sub-section-context" style="padding-bottom: 0px;">{{ space.name }}</div>
        </div>
        <Item v-if="appStore.currentSpaceId" routeName="campaign-selector" icon="bi-folder" :label="$t('app.sidebar.items.campaigns')"></Item>
        <div v-if="appStore.currentCampaignId && !isCampaignPending" class="sub-section">
          <div class="sub-section-context">{{ campaign.campaign.internal_name }}</div>
          <Item routeName="campaign" section="Overview" icon="bi-clipboard-data" :label="$t('app.sidebar.items.overview')"></Item>
          <Item routeName="campaign" section="Tracking" icon="bi-list-ul" :label="$t('app.sidebar.items.tracking')"></Item>
          <Item routeName="campaign" section="Results" icon="bi-pie-chart" :label="$t('app.sidebar.items.results')"></Item>
        </div>
        <div v-if="appStore.currentOrganisationId && canWriteMembers" class="sbar-separator">{{ $t('app.sidebar.sections.user') }}</div>
        <Item v-if="appStore.currentOrganisationId && canWriteMembers" routeName="users" icon="bi-person-gear" :label="$t('app.sidebar.items.userManagement')" data-step="manage-users"></Item>
      </div>
    </transition>
    <div class="sbar-footer">
      <div class="sbar-item tweaks">
        <div v-if="appStore.currentOrganisationId" class="sbar-separator">{{ $t('app.sidebar.sections.tools') }}</div>
        <div class="tweaks__item" :class="{ mobile: !appStore.sidebarIsOpen}" @click="router.push({ name: 'tutorial' })">
          <i class="bi bi-book"></i>
          <span v-if="appStore.sidebarIsOpen">{{ $t('app.sidebar.items.guide') }}</span>
        </div>
        <ThemeFooter></ThemeFooter>
        <LanguageFooter></LanguageFooter>
      </div>
      <div class="sbar-item profile-wrapper">
        <ProfileFooter data-step="account"></ProfileFooter>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDark } from '@vueuse/core';

import Item from '@/components/sidebarV2/Item.vue';
import ThemeFooter from '@/components/sidebarV2/ThemeFooter.vue';
import LanguageFooter from '@/components/sidebarV2/LanguageFooter.vue';
import ProfileFooter from '@/components/sidebarV2/ProfileFooter.vue';
import { useRoles } from '@/queries/user.query';
import { useCampaign } from '@/queries/campaigns.query';
import { useSpace } from '@/queries/space.query';
import { usePermissions } from '@/composables/permissions';

const isDark = useDark();
const router = useRouter();
const appStore = useAppStore();

const { canWriteMembers } = usePermissions();
const { data: roles } = useRoles();
const { data: campaign, isPending: isCampaignPending } = useCampaign();
const { data: space, isPending: isSpacePending } = useSpace();

</script>

<style lang="scss">
@import "@/assets/style/variables.scss";

  .tweaks__item {
    display: flex;
    gap: 6px;
    align-items: center;
    user-select: none;

    padding: 4px 8px;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      background-color: var(--primary-300-color);
      color: $secondary-color;
    }

    i {
      font-size: 14px;
    }

    span {
      font-weight: 500;
      font-size: 14px;
    }

    &.mobile {
      justify-content: center;

      i {
        font-size: 18px;
      }
    }
  }

  .dropdown__menu {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .dropdown__item {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      cursor: pointer;
      user-select: none;
  
      display: flex;
      align-items: center;
      gap: 8px;
  
      padding: 4px 8px;
      border-radius: 4px;
  
      &:hover {
        background-color: var(--primary-300-color);
        color: $secondary-color;
      }
    }
  }
</style>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.line {
  width: 2px;
  margin: 0px 10px;
  height: 26px;
  background-color: var(--light-grey-color);
}

.sbar-wrapper {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;

  background-color: var(--white-color);
  border-right: 1px solid var(--light-grey-color);

  width: 250px;
  height: 100vh;

  &.sbar-closed {
    width: 80px;

    .sbar-header {
      justify-content: center;
    }

    .sbar-body {
      gap: 6px;

      .sbar-separator {
        text-align: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 600;

        margin: 6px 0;

        &::after {
          display: none;
        }
      }

      ::v-deep(.sbar-item) {
        justify-content: center;
        padding: 10px 0px;

        .sbar-item-icon {
          font-size: 20px;
        }
      }
    }
  }

  .sbar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid var(--light-grey-color);
    padding: 0px 1rem;
    height: 3rem;

    .logo-small {
      width: 32px;
    }

    .logo {
      margin-bottom: -8px;
    }

    .sbar-toggle {
      font-size: 22px;
      padding: 4px;
      border-radius: 4px;

      &:hover {
        background-color: var(--primary-200-color);
      }
    }
  }

  .sbar-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 1rem;

    margin-top: 10px;

    overflow-x: hidden;
    overflow-y: auto;

    &::-webkit-scrollbar{
      display: none;
    }

    .sbar-separator {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      font-weight: 600;
      color: $secondary-200-color;
    }

    .sub-section {
      display: flex;
      flex-direction: column;
      gap: 6px;

      padding-left: 8px;
      margin-left: 15px;
      border-left: 1px solid $secondary-100-color;

      .sub-section-context {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
        font-weight: 600;
        padding-bottom: 2px;
        color: $secondary-200-color;

        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }
    }
  }

  .sbar-footer {
    display: flex;
    flex-direction: column;
    gap: 16px;

    padding: 0.5rem;
    margin-top: auto;

    .sbar-separator {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      font-weight: 600;
      color: $secondary-200-color;
      padding-left: 8px;
      padding-bottom: 8px;
    }
  }

  .tweaks {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .profile-wrapper {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding-top: 8px;

    border-top: 1px solid var(--light-grey-color);
  }
}
</style>